import { useCallback, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useMultipleJobs, usePrescriptionJob } from '../PrescriptionJobContext';
import {
  setBulkPrescriptionJob,
  setBulkPrescriptionJobs,
} from '../../../reducers/PrescriptionReducer';
import { isDemandLayerType } from '../../../model/surveys/country/SurveyCountryStrategy';
import { setNullIntervalForJob, setNullIntervalInIntervals } from '../nullIntervalJob';
import {
  getNullZoneAreaForField,
  hasSignificantNullZonesInField,
  isNullInterval,
  MIN_NULL_ZONE_AREA_FRACTION,
} from '../nullValueRegions';
import type Field from '../../../model/Field';
import type { Interval, PrescriptionJob } from '../PrescriptionJob';
import type { BulkPrescriptionJobs } from '../SeedingJob/SeedingJobCommonTypes';

export type NullZoneIntervalToggleProps = {
  selectedField: Field;
  isBulkPrescription: boolean;
  appliedInitialIntervals: Interval[] | null;
  bulkPrescriptionFields: Field[];
  bulkPrescriptionJobs: BulkPrescriptionJobs;
  hasMultipleJobs: boolean;
  updatePrescriptionJob: (changes: Partial<PrescriptionJob>, recalculate: boolean) => void;
};

type CachedNullArea = {
  field: Field;
  jobFieldId: PrescriptionJob['fieldId'];
  values: PrescriptionJob['values'];
  fieldSize: PrescriptionJob['fieldSize'];
  area: ReturnType<typeof getNullZoneAreaForField>;
};

const useNullZoneIntervalToggle = ({
  selectedField,
  isBulkPrescription,
  appliedInitialIntervals,
  bulkPrescriptionFields,
  bulkPrescriptionJobs,
  hasMultipleJobs,
  updatePrescriptionJob,
}: NullZoneIntervalToggleProps) => {
  const { prescriptionJob } = usePrescriptionJob();
  const {
    showCurrentJob: [isShowingCurrentJob],
  } = useMultipleJobs();
  const dispatch = useDispatch();
  const isOnBulkOverview = isBulkPrescription && !appliedInitialIntervals;
  const activeField: Field = useMemo(
    () =>
      prescriptionJob.usingJobPolygon
        ? {
            ...selectedField,
            fieldId: prescriptionJob.fieldId,
            polygon: prescriptionJob.polygon,
            bounds: prescriptionJob.bounds,
          }
        : selectedField,
    [
      selectedField,
      prescriptionJob.usingJobPolygon,
      prescriptionJob.fieldId,
      prescriptionJob.polygon,
      prescriptionJob.bounds,
    ]
  );
  const areaJob = isOnBulkOverview ? null : prescriptionJob;
  const areaField = isOnBulkOverview ? null : activeField;
  const cachedAreas = useRef(new Map<Field['fieldId'], CachedNullArea>());

  const getCachedArea = useCallback((job: PrescriptionJob | undefined, field: Field) => {
    if (!job) return getNullZoneAreaForField(job, field);
    const cached = cachedAreas.current.get(field.fieldId);
    if (
      cached?.field === field &&
      cached.jobFieldId === job.fieldId &&
      cached.values === job.values &&
      cached.fieldSize === job.fieldSize
    ) {
      return cached.area;
    }
    const area = getNullZoneAreaForField(job, field);
    cachedAreas.current.set(field.fieldId, {
      field,
      jobFieldId: job.fieldId,
      values: job.values,
      fieldSize: job.fieldSize,
      area,
    });
    return area;
  }, []);

  const hasNullZones = useMemo(() => {
    if (hasMultipleJobs && !isShowingCurrentJob) return false;
    let nullArea = 0;
    let fieldArea = 0;
    if (isOnBulkOverview) {
      const total = bulkPrescriptionFields.reduce(
        (total, field) => {
          const job = bulkPrescriptionJobs[field.fieldId];
          const area = getCachedArea(job, field);
          return {
            nullArea:
              total.nullArea + (job && !isDemandLayerType(job.layerType) ? area.nullArea : 0),
            fieldArea: total.fieldArea + area.fieldArea,
          };
        },
        { nullArea: 0, fieldArea: 0 }
      );
      nullArea = total.nullArea;
      fieldArea = total.fieldArea;
    } else if (!isDemandLayerType(prescriptionJob.layerType)) {
      const area = getCachedArea(prescriptionJob, activeField);
      nullArea = area.nullArea;
      fieldArea = area.fieldArea;
    }
    return fieldArea > 0 && nullArea > fieldArea * MIN_NULL_ZONE_AREA_FRACTION;
  }, [
    hasMultipleJobs,
    isShowingCurrentJob,
    isOnBulkOverview,
    bulkPrescriptionFields,
    bulkPrescriptionJobs,
    areaJob,
    areaField,
    getCachedArea,
  ]);

  const currentIntervals = isOnBulkOverview ? null : prescriptionJob.intervals;
  const nullZonesEnabled = useMemo(() => {
    if (!isOnBulkOverview) return currentIntervals?.some(isNullInterval) || false;
    return bulkPrescriptionFields.every((field) => {
      const job = bulkPrescriptionJobs[field.fieldId];
      if (!job || isDemandLayerType(job.layerType)) return true;
      const { nullArea, fieldArea } = getCachedArea(job, field);
      return (
        nullArea <= fieldArea * MIN_NULL_ZONE_AREA_FRACTION || job.intervals.some(isNullInterval)
      );
    });
  }, [
    isOnBulkOverview,
    bulkPrescriptionFields,
    bulkPrescriptionJobs,
    currentIntervals,
    getCachedArea,
  ]);

  const toggleNullZones = () => {
    const enabled = !nullZonesEnabled;
    if (isOnBulkOverview) {
      const jobs = { ...bulkPrescriptionJobs };
      Object.keys(jobs).forEach((fieldId) => {
        const jobId = Number(fieldId);
        const field = bulkPrescriptionFields.find((item) => String(item.fieldId) === fieldId);
        if (!field || isDemandLayerType(jobs[jobId].layerType)) return;
        jobs[jobId] = setNullIntervalForJob(
          jobs[jobId],
          field,
          enabled && hasSignificantNullZonesInField(jobs[jobId], field)
        );
      });
      dispatch(setBulkPrescriptionJobs(jobs));
      updatePrescriptionJob(
        {
          intervals: setNullIntervalInIntervals(prescriptionJob.intervals, enabled),
        },
        false
      );
    } else {
      const nextJob = setNullIntervalForJob(prescriptionJob, activeField, enabled);
      updatePrescriptionJob({ values: nextJob.values, intervals: nextJob.intervals }, true);
      if (isBulkPrescription) dispatch(setBulkPrescriptionJob(prescriptionJob.fieldId, nextJob));
    }
  };

  return { hasNullZones, nullZonesEnabled, isOnBulkOverview, toggleNullZones };
};

export default useNullZoneIntervalToggle;
